import type { ComponentProps } from 'react';
import React, { useRef } from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  FlexDirectionProps,
  FlexProps,
  HeightProps,
  MaxWidthProps,
  OpacityProps,
  PositionProps,
  SpaceProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  borderRadius,
  borderTop,
  boxShadow,
  display,
  flex,
  flexDirection,
  height,
  maxWidth,
  minWidth,
  opacity,
  position,
  space,
  top,
  width,
  zIndex,
} from 'styled-system';

import { SEARCH_SUGGESTIONS_Z_INDEX } from '../../constants/z-index';
import type {
  BoxSizingProps,
  ListStyleProps,
  OverflowYProps,
  TransitionPropertyProps,
  TransitionProps,
  TransitionTimingFunctionProps,
} from '../../theme/system-utilities';
import {
  borderTopColor,
  borderTopStyle,
  borderTopWidth,
  boxSizing,
  listStyle,
  overflowY,
  transitionProperty,
  transitionTimingFunction,
} from '../../theme/system-utilities';
import useGetCenterProps from './hooks/center-props';
import type { SuggestionsListContainerProps } from './types';
import getListStyles from './utils/get-list-styles';

type ContainerProps = Pick<SuggestionsListContainerProps, 'id' | 'role' | 'aria-labelledby'>;

type ContainerStyledProps = DisplayProps &
  ListStyleProps &
  OverflowYProps &
  FlexProps &
  FlexDirectionProps &
  PositionProps &
  WidthProps &
  MaxWidthProps &
  HeightProps &
  TopProps &
  SpaceProps &
  BorderRadiusProps &
  BoxShadowProps &
  BoxSizingProps &
  ZIndexProps &
  BackgroundColorProps &
  TransitionProps &
  TransitionTimingFunctionProps &
  TransitionPropertyProps &
  OpacityProps;

const UnstyledContainer = styled.div<ContainerProps & ContainerStyledProps>`
  -webkit-appearance: none;
  overscroll-behavior-y: contain;
  ${display};
  ${listStyle};
  ${overflowY};
  ${position};
  ${flex}
  ${flexDirection}
  ${top};
  ${borderTopStyle};
  ${borderTopWidth};
  ${borderTopColor};
  ${borderTop};
  ${borderRadius};
  ${boxShadow};
  ${width};
  ${minWidth};
  ${height};
  ${maxWidth};
  ${boxSizing};
  ${zIndex};
  ${space};
  ${transitionProperty};
  ${transitionTimingFunction};
  ${opacity};
  ${backgroundColor};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    listStyle="none"
    overflowY="auto"
    flex="1"
    position={[null, 'absolute']}
    width={1}
    height={['calc(100% - 68px)', 'auto']}
    m="sp_0"
    boxSizing="border-box"
    zIndex={[null, SEARCH_SUGGESTIONS_Z_INDEX]}
    backgroundColor="sys.neutral.background.default"
    transitionProperty="opacity, box-shadow"
    boxShadow="0 0 0 0 rgba(44, 54, 56, 0.3)"
    borderRadius={[null, 'large']}
    flexDirection={['column', null, 'row']}
    px="sp_8"
    pt="sp_8"
    top="calc(100% + 12px)"
    {...props}
  />
);

const UnstyledInnerContainer = styled.div<DisplayProps & WidthProps>`
  ${display};
  ${width};
`;

const InnerContainer = (props: ComponentProps<typeof UnstyledInnerContainer>) => (
  <UnstyledInnerContainer display={['block', null, 'flex']} width="100%" {...props} />
);

const SuggestionsListContainer = ({
  hasExtraPadding,
  timeout,
  isBodyOverlayVisible,
  children,
  open,
  containerRef,
  isSingleColumn,
  inputContainerRef,
  ...props
}: SuggestionsListContainerProps) => {
  const listRef = useRef(null);
  const centerAutoSuggestProps = useGetCenterProps({
    listRef,
    inputContainerRef,
    open,
    isSingleColumn,
  });
  const styles = getListStyles({
    hasExtraPadding,
    timeout,
    isBodyOverlayVisible,
    open,
    isSingleColumn,
  });

  return (
    <Container
      {...props}
      {...styles}
      {...centerAutoSuggestProps}
      data-testid="suggestions-list"
      data-cnstrc-autosuggest=""
      ref={containerRef}
    >
      <InnerContainer data-testid="suggestions-list-inner-container" ref={listRef}>
        {children}
      </InnerContainer>
    </Container>
  );
};

export default SuggestionsListContainer;
